import { createRoute, lazyRouteComponent } from '@tanstack/react-router'
import { publicWithTabsRoute } from '../_publicWithTabs'
import { restaurantKeys } from '@/api/keys'
import { fetchRestaurantSummary } from '@/api/queries/useRestaurantQueries'
import { retryUnlessClientError } from '@/api/queries/restaurantDetailRetry'
import { RestaurantProfileSkeleton } from '@/components/skeletons/RestaurantProfileSkeleton'
import { loadRestaurantProfileChunk } from '@/utils/restaurantProfileChunk'

/**
 * `lazyRouteComponent` (not a bare `React.lazy`) so the component carries
 * `.preload`: the router awaits the chunk while it loads the route, i.e. BEFORE
 * it starts the view transition, so the transition's "new" snapshot already
 * contains the hero and its shared photo/title names. The chunk is also warmed
 * at idle from Search (`warmRestaurantProfileChunk`); both share one download.
 */
const RestaurantProfile = lazyRouteComponent(loadRestaurantProfileChunk, 'RestaurantProfile')

// Restaurant route - accessible both authenticated and unauthenticated (for sharing)
// Uses publicWithTabsRoute so it has the tab bar
export const restaurantRoute = createRoute({
  getParentRoute: () => publicWithTabsRoute,
  path: '/restaurants/$restaurantId',
  parseParams: ({ restaurantId }) => ({ restaurantId: Number(restaurantId) }),
  stringifyParams: ({ restaurantId }) => ({ restaurantId: String(restaurantId) }),
  loader: async ({ context, params }) => {
    const { queryClient } = context
    const restaurantId = params.restaurantId
    const query = {
      queryKey: restaurantKeys.detail(restaurantId),
      queryFn: ({ signal }: { signal: AbortSignal }) => fetchRestaurantSummary(restaurantId, { signal }),
      staleTime: 30000, // 30 seconds
    }

    // Warm path: the detail cache already holds data (seeded from the tapped
    // card's list item by `seedRestaurantProfile`, or a profile viewed earlier).
    // Open at once and refresh in the background. Not awaited, and it
    // retries like the page's own `useQuery` (which joins this in-flight fetch,
    // so this fetch's retry policy is the one that applies): default retries,
    // except a 4xx such as a 404 fails at once so the page can redirect.
    if (queryClient.getQueryData(query.queryKey) !== undefined) {
      void queryClient.prefetchQuery({ ...query, retry: retryUnlessClientError })
      return
    }

    // Cold path (deep link, refresh, or a tap with no list data): the page
    // cannot render without the summary, so wait for it. `retry: 0` because
    // the default (2 retries with backoff) froze the click for ~3s on a failing
    // request. `prefetchQuery` never throws; on failure the entry is left in an
    // error state with no data, so the page's `useQuery` mounts and refetches
    // with the client default retry. Recovery is unchanged, only the stall is gone.
    await queryClient.prefetchQuery({ ...query, retry: 0 })
  },
  // Safety net for the cold path: a slow loader shows a skeleton instead of a
  // dead click. Seeded taps never reach `pendingMs` (the loader returns at
  // once). Note the router starts the view transition when the pending state
  // commits, so a pending commit captured as the "new" snapshot would lose
  // the morph; RestaurantCard therefore only requests a view transition when the
  // chunk and the seed are both ready (see useOpenRestaurantProfile).
  pendingComponent: RestaurantProfileSkeleton,
  pendingMs: 150,
  pendingMinMs: 0,
  component: RestaurantProfile,
})
